import { TForm } from '@/components/forms/form-participant-data-vp/FormParticipantDataVp.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/ui/form';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import formActivityConsumerSchema from '@/schemas/formActivityConsumer';
import { TConsumer } from '@/services/consumer';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import FormUploadConsumerImage from '../form-uploadConsumer-image/FormUploadConsumerImage';
import AdditionalDataForm from './components/AdditionalDataForm';
import ConsumerForm from './components/ConsumerForm';
import EmergencyContactForm from './components/EmergencyContactForm';
import ParentsForm from './components/ParentsForm';

const getDefaultParentsArr = (parents?: any[]): any[] => {
  const parsedParents = parents?.filter((p) => !!p) || [];
  const parentObj = {
    firstName: '',
    lastName: '',
    phone: '',
    identification: {
      number: '',
      type: ''
    },
    email: '',
    address: '',
    location: '',
    postalCode: '',
    connection: ''
  };
  if (!parsedParents || parsedParents?.length === 0) {
    return Array.from({ length: 2 }, () => ({ ...parentObj }));
  } else if (parsedParents?.length === 1) {
    return [...parsedParents, parentObj];
  } else return parsedParents;
};

const FormActivityConsumer: React.FC<{
  consumer: TConsumer;
  onSubmit: (data: TForm) => void;
  isSending: boolean;
}> = ({ consumer, onSubmit, isSending }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const defaultValues = {
    consumer: {
      firstName: consumer?.firstName || '',
      lastName: consumer?.lastName || '',
      identification: {
        number: consumer?.identification?.number || '',
        type: consumer?.identificationType?.id || ''
      },
      birthdate: consumer?.birthdate ? new Date(consumer.birthdate) : undefined,
      location: consumer?.location || '',
      phone: consumer?.phone || '',
      email: consumer?.email || ''
    },
    emergencyContact: {
      fullName: consumer?.emergencyContact?.fullName || '',
      phone: consumer?.emergencyContact?.phone || ''
    },
    parents: getDefaultParentsArr(consumer?.additionalData?.parents),
    additionalData: {
      illness: consumer?.additionalData?.illness || {
        value: false,
        answer: ''
      },
      acceptDataPrivacy: consumer?.additionalData?.acceptDataPrivacy || false,
      acceptTakePhotos: consumer?.additionalData?.acceptTakePhotos || false,
      authOfferProducts: consumer?.additionalData?.authOfferProducts || false,
      group: consumer?.additionalData?.group || null
    },
    observations: consumer?.additionalData?.observations || ''
  };

  const form = useForm<TForm>({
    resolver: zodResolver(formActivityConsumerSchema({ minAge: 0 })),
    defaultValues
  });

  const hasChanged = !form.formState.isDirty;

  return (
    <Card className="w-full p-8 mb-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Tabs defaultValue="participant">
            <ScrollArea>
              <TabsList className="mb-4">
                <TabsTrigger value="participant">
                  {t('activity.student.personalData')}
                </TabsTrigger>
                <TabsTrigger value="emergencyContact">
                  {t('activity.formEdit.emergencyContact.tab')}
                </TabsTrigger>
                <TabsTrigger value="parents">{t('responsible')}</TabsTrigger>
                <TabsTrigger value="additionalData">
                  {t('activity.formEdit.additionalData.tab')}
                </TabsTrigger>
                <TabsTrigger value="observations">
                  {t('activity.table.observation')}
                </TabsTrigger>
                <TabsTrigger value="documents">
                  {t('common.documents')}
                </TabsTrigger>
              </TabsList>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>

            <TabsContent value="participant">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {t('activity.student.personalData')}
                </h2>
                <ConsumerForm form={form} />
              </div>
            </TabsContent>

            <TabsContent value="emergencyContact">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {t('activity.formEdit.emergencyContact.title')}
                </h2>
                <EmergencyContactForm form={form} />
              </div>
            </TabsContent>

            <TabsContent value="parents">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">{t('responsible')}</h2>
                <ParentsForm form={form} />
              </div>
            </TabsContent>

            <TabsContent value="additionalData">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {t('activity.formEdit.additionalData.title')}
                </h2>
                <AdditionalDataForm form={form} />
              </div>
            </TabsContent>

            <TabsContent value="observations">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {t('activity.table.observation')}
                </h2>
                <FormField
                  control={form.control}
                  name={'observations'}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </TabsContent>
            <TabsContent value="documents">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {t('common.documents')}
                </h2>
                <FormUploadConsumerImage id={consumer.id} />
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex flex-col-reverse justify-end gap-4 !mb-8 sm:flex-row">
            <Button type="button" variant="outline" asChild>
              <Link
                to={'..'}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(-1);
                }}
                className="flex items-center gap-2 "
              >
                {t('common.cancel')}
              </Link>
            </Button>
            <Button disabled={isSending || hasChanged}>{t('form.save')}</Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};

export default FormActivityConsumer;
